/**
 * Appello (V14), la scheda Appello: le presenze, solo il giorno della seduta.
 *
 * Una riga compatta per giocatore con i tre stati in un gruppo di toggle
 * (solo icone nelle righe strette), salvataggio a ogni tocco. Sopra la rosa i
 * conteggi, lo stato del salvataggio e i comandi. Alla prima apertura
 * utile, se non ci sono presenze, i noti infortunati e gli altri presenti sono
 * già segnati. "Tutti presenti" non sovrascrive chi ha già uno stato. Chiudi
 * appello, con conferma, blocca gruppi e presenze, segna svolto e compila il
 * Registro seduta. La coda (`comuni/coda.ts`) manda una richiesta per riga
 * alla volta e ritenta da sola gli errori di rete.
 */

import { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import type { StatoPresenza } from '@calcio/shared';
import { dataLocale, puo, STATI_PRESENZA } from '@calcio/shared';
import { toast } from 'sonner';
import { CheckCheckIcon, ClipboardCheckIcon, InfoIcon, LockIcon } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { cn } from '@/lib/utils';
import { PulsanteInvio } from '../comuni/Azioni.js';
import { chiama } from '../api/client.js';
import { messaggio, useDati } from '../comuni/dati.js';
import { StatoCaricamento } from '../comuni/Testata.js';
import { useCodaRighe } from '../comuni/useCodaRighe.js';
import { useContesto } from '../navigazione/contesto.js';
import { NonTrovata } from '../navigazione/NonTrovata.js';
import {
  type DettaglioAllenamento,
  ETICHETTA_PRESENZA,
  ICONA_PRESENZA,
  type StatoAllenamento,
  nomeInRosa,
  riepiloga,
} from './allenamenti.js';
import { BarraScheda, CardVista, StatoSalvataggio } from '../comuni/Riquadri.js';
import { useAllenamento } from './contesto.js';
import { Maglia } from '../comuni/Maglia.js';
import { useConferma } from '../comuni/Conferma.js';
import { StatoVuoto } from '../comuni/StatoVuoto.js';
import { Nota } from '../comuni/Nota.js';

export function Appello() {
  const { ss, id } = useParams<{ ss: string; id: string }>();
  const { utente } = useContesto();
  const gestisce = puo(utente.assegnazioni, 'allenamento.gestisci', ss);
  const { dati, errore } = useDati<DettaglioAllenamento>(
    ss && puo(utente.assegnazioni, 'presenza.scrivi', ss) ? `/allenamenti/${id}/appello` : '',
  );

  if (!ss || !puo(utente.assegnazioni, 'presenza.scrivi', ss)) return <NonTrovata />;
  if (errore) return <StatoCaricamento errore={errore} />;
  if (!dati || dati.allenamento.id !== id) return <StatoCaricamento errore={null} />;
  if (dati.allenamento.squadraStagioneId !== ss) {
    return (
      <Navigate
        to={`/sq/${dati.allenamento.squadraStagioneId}/allenamenti/${id}/presenze`}
        replace
      />
    );
  }
  return <Elenco key={id} dettaglio={dati} gestisce={gestisce} />;
}

function Elenco({ dettaglio, gestisce }: { dettaglio: DettaglioAllenamento; gestisce: boolean }) {
  const a = dettaglio.allenamento;
  const [stato, setStato] = useState<StatoAllenamento>(a.stato);
  const allenamento = useAllenamento();
  const aggiornaStato = allenamento?.aggiornaStato;
  // Il badge della testata segue lo stato: il primo tocco o Chiudi appello lo cambiano.
  useEffect(() => {
    if (stato !== a.stato) aggiornaStato?.(stato);
  }, [stato, a.stato, aggiornaStato]);
  const [tuttiInCorso, setTuttiInCorso] = useState(false);
  const [chiudiInCorso, setChiudiInCorso] = useState(false);
  const [chiedi, conferma] = useConferma();
  const arrivato = dataLocale(a.dataOra) <= dataLocale(new Date());
  const chiuso = stato !== 'programmato';

  const c = useCodaRighe<StatoPresenza>(
    () => Object.fromEntries(dettaglio.rosa.map((g) => [g.id, g.presenza])),
    async (giocatoreId, voluto) => {
      const esito = await chiama<{ stato: StatoAllenamento }>(
        `/allenamenti/${a.id}/presenze/${giocatoreId}`,
        voluto ? { metodo: 'PUT', corpo: { stato: voluto } } : { metodo: 'DELETE' },
      );
      setStato(esito.stato);
    },
    (rifiuto) => toast.error(rifiuto),
  );
  const inSospeso = c.inSospeso();

  // Il gruppo a scelta singola manda la stringa vuota quando si ritocca lo stato acceso.
  const tocca = (giocatoreId: string, scelto: string) => {
    c.imposta(giocatoreId, scelto === '' ? null : (scelto as StatoPresenza));
  };

  const tuttiPresenti = async () => {
    setTuttiInCorso(true);
    try {
      const esito = await chiama<{
        stato: StatoAllenamento;
        presenze: Record<string, StatoPresenza>;
      }>(`/allenamenti/${a.id}/presenze/tutti-presenti`, { metodo: 'POST' });
      setStato(esito.stato);
      c.allinea(esito.presenze);
    } catch (e) {
      toast.error(messaggio(e));
    } finally {
      setTuttiInCorso(false);
    }
  };

  const chiudi = async () => {
    const si = await chiedi({
      titolo: "Chiudere l'appello?",
      testo:
        "Gruppi e presenze si bloccano, l'allenamento diventa svolto e il Registro seduta si compila per i presenti.",
      azione: "Chiudi l'appello",
    });
    if (!si) return;
    setChiudiInCorso(true);
    try {
      const esito = await chiama<{ stato: StatoAllenamento }>(
        `/allenamenti/${a.id}/chiudi-appello`,
        { metodo: 'POST' },
      );
      setStato(esito.stato);
      toast.success('Appello chiuso.');
    } catch (e) {
      toast.error(messaggio(e));
    } finally {
      setChiudiInCorso(false);
    }
  };

  const rilevati = dettaglio.rosa.filter((g) => c.valore(g.id) !== null).length;
  const nonSalvate = dettaglio.rosa.filter((g) => c.esito(g.id) === 'non_salvato').length;
  const r = riepiloga(dettaglio.rosa.map((g) => ({ presenza: c.valore(g.id) })));
  const bloccato = chiuso || !arrivato;

  return (
    <>
      <BarraScheda
        comandi={
          <>
            <PulsanteInvio
              type="button"
              variant="outline"
              inCorso={tuttiInCorso}
              disabled={bloccato || dettaglio.rosa.length === 0}
              onClick={() => void tuttiPresenti()}
            >
              <CheckCheckIcon data-icon="inline-start" />
              Tutti presenti
            </PulsanteInvio>
            {gestisce && stato === 'programmato' && arrivato && (
              <PulsanteInvio
                type="button"
                inCorso={chiudiInCorso}
                disabled={
                  inSospeso > 0 || dettaglio.rosa.length === 0 || rilevati < dettaglio.rosa.length
                }
                onClick={() => void chiudi()}
              >
                <LockIcon data-icon="inline-start" />
                Chiudi appello
              </PulsanteInvio>
            )}
          </>
        }
      >
        {STATI_PRESENZA.map((s) => {
          const Icona = ICONA_PRESENZA[s];
          const n = s === 'presente' ? r.presenti : s === 'assente' ? r.assenti : r.infortunati;
          return (
            <span
              key={s}
              className="flex items-center gap-1 text-muted-foreground"
              title={ETICHETTA_PRESENZA[s]}
            >
              <Icona aria-hidden className="size-4" />
              <span className="font-semibold text-foreground tabular-nums">{n}</span>
              <span className="hidden @xl:inline">{ETICHETTA_PRESENZA[s].toLowerCase()}</span>
            </span>
          );
        })}
        {!bloccato && (
          <StatoSalvataggio
            inSospeso={inSospeso}
            nonSalvate={nonSalvate}
            onRiprova={() => c.riprova()}
          />
        )}
      </BarraScheda>

      {stato === 'annullato' && (
        <Alert>
          <InfoIcon />
          <AlertTitle>Allenamento annullato</AlertTitle>
          <AlertDescription>Ripristinalo dal riepilogo per modificare l'appello.</AlertDescription>
        </Alert>
      )}
      {stato === 'programmato' && !arrivato && (
        <Alert>
          <InfoIcon />
          <AlertTitle>Appello non ancora aperto</AlertTitle>
          <AlertDescription>L'appello si fa il giorno della seduta.</AlertDescription>
        </Alert>
      )}
      {stato === 'svolto' && (
        <Alert>
          <InfoIcon />
          <AlertTitle>Appello chiuso</AlertTitle>
          <AlertDescription>
            Le presenze non si modificano. Minuti e RPE si correggono nel Registro seduta.
          </AlertDescription>
        </Alert>
      )}

      <CardVista
        titolo="Rosa"
        Icona={ClipboardCheckIcon}
        conteggio={`${rilevati}/${dettaglio.rosa.length}`}
      >
        {!bloccato && (
          <Nota className="px-1">
            Chi resta senza stato è non rilevato, che non è un&apos;assenza; ritocca uno stato per
            toglierlo. Infortunato vale solo per questo allenamento.
          </Nota>
        )}
        {dettaglio.rosa.length === 0 ? (
          <StatoVuoto titolo="Nessun giocatore in rosa a questa data" />
        ) : (
          <ul className="grid gap-2 @3xl:grid-cols-2">
            {dettaglio.rosa.map((g) => {
              const valore = c.valore(g.id);
              const esito = c.esito(g.id);
              return (
                <li
                  key={g.id}
                  className="flex items-center gap-3 rounded-lg border border-border bg-card p-2 @container"
                >
                  <Maglia numero={g.numero} ruolo={g.ruoloPreferito} />
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate font-semibold">
                      {g.cognome} {g.nome}
                    </span>
                    <span
                      className={cn(
                        'text-[13px]',
                        esito === 'non_salvato'
                          ? 'font-semibold text-destructive'
                          : 'text-muted-foreground',
                      )}
                    >
                      {esito === 'invio'
                        ? 'Salvo…'
                        : esito === 'non_salvato'
                          ? 'Non salvato'
                          : valore
                            ? ETICHETTA_PRESENZA[valore]
                            : 'Non rilevato'}
                    </span>
                  </span>
                  <ToggleGroup
                    type="single"
                    variant="outline"
                    size="sm"
                    spacing={0}
                    value={valore ?? ''}
                    disabled={bloccato}
                    onValueChange={(v) => tocca(g.id, v)}
                    aria-label={`Presenza di ${nomeInRosa(g)}`}
                  >
                    {STATI_PRESENZA.map((s) => {
                      const Icona = ICONA_PRESENZA[s];
                      return (
                        <ToggleGroupItem
                          key={s}
                          value={s}
                          aria-label={ETICHETTA_PRESENZA[s]}
                          title={ETICHETTA_PRESENZA[s]}
                          className={ACCESO[s]}
                        >
                          <Icona />
                          <span className="hidden @md:inline">{ETICHETTA_PRESENZA[s]}</span>
                        </ToggleGroupItem>
                      );
                    })}
                  </ToggleGroup>
                </li>
              );
            })}
          </ul>
        )}
      </CardVista>
      {conferma}
    </>
  );
}

/** Lo stato acceso prende il colore della presenza (`COLORE_PRESENZA`). */
const ACCESO: Record<StatoPresenza, string> = {
  presente:
    'data-[state=on]:border-stato-disponibile data-[state=on]:bg-stato-disponibile data-[state=on]:text-white',
  assente:
    'data-[state=on]:border-stato-recupero data-[state=on]:bg-stato-recupero data-[state=on]:text-black',
  infortunato:
    'data-[state=on]:border-stato-infortunato data-[state=on]:bg-stato-infortunato data-[state=on]:text-white',
};
